import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { Task, TaskResponse } from "@/models/api/administration/taskScheduler/Task";
import { TaskEdit } from "@/models/api/administration/taskScheduler/TaskEdit";
import {
  getTaskTable,
  restartTask,
  runTaskManually,
  getTaskType,
  editTaskType,
  getTaskUsers,
  editTaskUsers,
  getUserTasks,
  toggleTask,
  toggleUserTask,
} from "@/requests/administration/administrationTaskScheduler";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { User } from "@/models/api/administration/users/User";

export interface AdministrationTaskSchedulerStateInterface {
  taskItems: Array<Task>;
  taskCount: number | null;
  taskFilters: null | Array<CriterionBase>;
  taskOptions: DataOptions;
  recordStampId: number | null;

  isEditDialog: boolean;
  taskEditId: null | number;
  taskEditModel: TaskEdit;

  users: Array<User>;
  pickedUsers: Array<User>;
  isUserDialog: boolean;

  userTaskItems: Array<Task>;
  userTaskCount: number | null;
  userTaskFilters: null | Array<CriterionBase>;
  userTaskOptions: DataOptions;
}

export const AdministrationTaskSchedulerState: AdministrationTaskSchedulerStateInterface = {
  taskItems: [],
  taskCount: null,
  taskFilters: null,
  taskOptions: new DefaultDataOptions(),
  recordStampId: null,

  taskEditModel: new TaskEdit(),
  taskEditId: null,
  isEditDialog: false,

  users: [],
  pickedUsers: [],
  isUserDialog: false,

  userTaskItems: [],
  userTaskCount: null,
  userTaskFilters: null,
  userTaskOptions: new DefaultDataOptions(),
};

const AdministrationTaskSchedulerModule = defineModule({
  state: AdministrationTaskSchedulerState as AdministrationTaskSchedulerStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setTaskFilters(state, param: Array<CriterionBase>) {
      state.taskFilters = param;
    },
    setTaskOptions(state, param: DataOptions) {
      state.taskOptions = param;
    },
    setTasks(state, param: TaskResponse) {
      state.taskItems = param.items;
      state.taskCount = param.totalCount;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },

    setTaskEditModel(state, param: TaskEdit) {
      state.taskEditModel = param;
    },
    setTaskEditId(state, param: null | number) {
      state.taskEditId = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },

    setUsers(state, param: Array<User>) {
      state.users = param;
    },
    setPickedUsers(state, param: Array<User>) {
      state.pickedUsers = param;
    },
    setIsUserDialog(state, param: boolean) {
      state.isUserDialog = param;
    },

    setUserTaskFilters(state, param: Array<CriterionBase>) {
      state.userTaskFilters = param;
    },
    setUserTaskOptions(state, param: DataOptions) {
      state.userTaskOptions = param;
    },
    setUserTasks(state, param: TaskResponse) {
      state.userTaskItems = param.items;
      state.userTaskCount = param.totalCount;
    },
  },
  actions: {
    async downloadTasks(context) {
      const { commit, state } = _moduleActionContext(context);
      const tasks = await getTaskTable({
        count: state.taskOptions.itemsPerPage,
        page: state.taskOptions.page - 1,
        criterions: _.cloneDeep(state.taskFilters),
        filterName: FILTER_NAMES.TaskSchedulerStandardFilter,
      });
      commit.setTasks(tasks);
    },
    async restartTask(context, param: { taskId: number; taskName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isRestartTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToRestartTask")) + "\n" + param.taskName,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isRestartTaskConfirmed) {
        await restartTask(param.taskId);
        await dispatch.downloadTasks();
      }
    },
    async lockTask(context, param: { taskId: number; taskName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isLockTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToLockTask")) + "\n" + param.taskName,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isLockTaskConfirmed) {
        await toggleTask(param.taskId);
        await dispatch.downloadTasks();
      }
    },
    async unlockTask(context, param: { taskId: number; taskName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isUnlockTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToUnlockTask")) + "\n" + param.taskName,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isUnlockTaskConfirmed) {
        await toggleTask(param.taskId);
        await dispatch.downloadTasks();
      }
    },
    async lockUserTask(context, param: { taskId: number; taskName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isLockTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToLockTask")) + "\n" + param.taskName,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isLockTaskConfirmed) {
        await toggleUserTask(param.taskId);
        await dispatch.downloadUserTasks();
      }
    },
    async unlockUserTask(context, param: { taskId: number; taskName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isUnlockTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToUnlockTask")) + "\n" + param.taskName,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isUnlockTaskConfirmed) {
        await toggleUserTask(param.taskId);
        await dispatch.downloadUserTasks();
      }
    },
    async runTaskManually(context, taskId: number) {
      const { dispatch } = _moduleActionContext(context);
      await runTaskManually(taskId);
      await dispatch.downloadTasks();
    },
    async editTasks(context, taskId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setTaskEditModel(await getTaskType(taskId));
      commit.setTaskEditId(taskId);
      commit.setIsEditDialog(true);
    },
    async saveTaskType(context) {
      const { commit, state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.taskEditId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await editTaskType(state.taskEditModel, state.taskEditId);
      }
      commit.setIsEditDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      await dispatch.downloadTasks();
    },
    async openUserEditor(context, task: Task) {
      const { commit } = _moduleActionContext(context);
      const response = await getTaskUsers();
      commit.setUsers(response.filter((x) => !task.users.includes(x.id)));
      commit.setPickedUsers(response.filter((x) => task.users.includes(x.id)));
      commit.setTaskEditId(task.id);
      commit.setIsUserDialog(true);
    },
    async editTaskUser(context) {
      const { commit, state, dispatch } = _moduleActionContext(context);
      if (state.taskEditId) {
        await editTaskUsers(
          state.taskEditId,
          state.pickedUsers.map((x) => x.id)
        );
        commit.setTaskEditId(null);
        await dispatch.downloadTasks();
      }
    },
    async downloadUserTasks(context) {
      const { commit, state } = _moduleActionContext(context);
      const tasks = await getUserTasks({
        count: state.userTaskOptions.itemsPerPage,
        page: state.userTaskOptions.page - 1,
        criterions: _.cloneDeep(state.userTaskFilters),
        filterName: FILTER_NAMES.TaskSchedulerStandardFilter,
      });
      commit.setUserTasks(tasks);
    },
  },
});

export default AdministrationTaskSchedulerModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationTaskSchedulerModule);
